import { useEffect } from "react";
import { getRouteSEO, BASE_URL, DEFAULT_OG_IMAGE, generateSchemaJSON } from "@/lib/seo-meta";

interface SEOHeadProps {
  pathname?: string;
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
}

export function SEOHead({
  pathname,
  title,
  description,
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
  canonical,
}: SEOHeadProps) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentPath = pathname || window.location.pathname;
    const defaultSEO = getRouteSEO(currentPath);

    const activeTitle = title || defaultSEO.title;
    const activeDesc = description || defaultSEO.desc;
    const activeKeywords = keywords || defaultSEO.keywords;
    const activeCanonical = canonical || `${BASE_URL}${defaultSEO.path === "/" ? "" : defaultSEO.path}`;
    const activeOgImage = ogImage || DEFAULT_OG_IMAGE;

    // 1. Update Document Title
    document.title = activeTitle;

    // Helper to set or create meta tag
    const setMeta = (attributeName: "name" | "property", attributeValue: string, content: string) => {
      let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Helper to set or create link tag
    const setLink = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute(rel, rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // 2. Standard Meta Tags
    setMeta("name", "description", activeDesc);
    setMeta("name", "keywords", activeKeywords);
    setMeta("name", "author", "KMWAdvertising LLP");
    setMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // 3. Open Graph Tags (WhatsApp, Facebook, LinkedIn)
    setMeta("property", "og:title", activeTitle);
    setMeta("property", "og:description", activeDesc);
    setMeta("property", "og:url", activeCanonical);
    setMeta("property", "og:image", activeOgImage);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "KMWAdvertising LLP");
    setMeta("property", "og:locale", "en_US");

    // Remove any leftover Twitter card meta tags
    const twitterTags = document.querySelectorAll('meta[name^="twitter:"]');
    twitterTags.forEach((t) => t.remove());

    // 4. Canonical Link
    setLink("canonical", activeCanonical);

    // 5. JSON-LD Schema
    const schemaData = generateSchemaJSON({
      path: currentPath,
      title: activeTitle,
      desc: activeDesc,
      keywords: activeKeywords,
    });

    let schemaScript = document.querySelector('script[type="application/ld+json"]#seo-schema');
    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.setAttribute("type", "application/ld+json");
      schemaScript.setAttribute("id", "seo-schema");
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schemaData, null, 2);
  }, [pathname, title, description, keywords, ogImage, canonical]);

  return null;
}
